import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { t } from '../theme';
import { useStore } from '../store';

/**
 * A focused real <input> in a sheet, rather than an app-drawn keyboard.
 *
 * The panel is a tablet, so the OS keyboard is right there — and it brings
 * paste, dictation, autocorrect, long-press umlauts, an external Bluetooth
 * keyboard and screen-reader support, none of which a grid of divs can offer.
 * Paste alone decides it: a secret iCal URL is not something anyone should
 * type by hand.
 *
 * Structured input keeps its purpose-built pads (the date keypad on Prüfen,
 * the code pad on the Gate) — those are constrained editors, not keyboards.
 */
const MAX_LENGTH = 2048;

export function TextPrompt() {
  const prompt = useStore((s) => s.prompt);
  const closePrompt = useStore((s) => s.closePrompt);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState('');

  const url = prompt?.mode === 'url';
  const numeric = prompt?.mode === 'numeric';
  const secret = prompt?.mode === 'password';

  useLayoutEffect(() => {
    if (!prompt) return;
    setValue(prompt.value);
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // Land the caret at the end for an edit, but select all for a fresh URL
    // so a paste replaces the https:// seed rather than appending to it.
    if (url) input.select();
    else input.setSelectionRange(prompt.value.length, prompt.value.length);
  }, [prompt, url]);

  /**
   * iOS resizes only the *visual* viewport for the keyboard, so a bottom-
   * anchored sheet would sit underneath it. Track the inset and lift the sheet.
   */
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setKeyboardInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  if (!prompt) return null;

  const commit = () => prompt.onCommit(value.trim());

  return (
    <div
      onClick={closePrompt}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.72)',
        backdropFilter: 'blur(8px)',
        animation: 'fade 140ms ease-out',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '30px 34px',
        paddingBottom: 30 + keyboardInset,
        transition: 'padding-bottom 160ms ease-out',
        zIndex: 30,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          borderRadius: 26,
          background: 'oklch(0.22 0.012 60)',
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: '20px 22px 22px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: 'oklch(0.66 0.015 70)' }}>
          {prompt.label}
        </div>

        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, MAX_LENGTH))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') closePrompt();
          }}
          type={secret ? 'password' : url ? 'url' : 'text'}
          inputMode={url ? 'url' : numeric ? 'numeric' : 'text'}
          autoComplete={secret ? 'current-password' : 'off'}
          enterKeyHint="done"
          autoCorrect={url || numeric || secret ? 'off' : 'on'}
          autoCapitalize={url || numeric || secret ? 'none' : 'sentences'}
          spellCheck={!url && !numeric && !secret}
          placeholder={url ? 'https://…' : ''}
          style={{
            width: '100%',
            background: 'oklch(0.17 0.012 60)',
            border: '1px solid oklch(1 0 0 / 0.1)',
            borderRadius: 18,
            padding: '18px 20px',
            color: t.ink,
            caretColor: t.accent,
            fontFamily: url ? t.mono : t.sans,
            fontSize: url ? 22 : 30,
            fontWeight: url ? 500 : 600,
            letterSpacing: url ? 0 : '-0.02em',
            outline: 'none',
          }}
        />

        <div style={{ display: 'flex', gap: 12 }}>
          <div
            className="press"
            onClick={closePrompt}
            style={{
              height: 72,
              padding: '0 30px',
              flex: 'none',
              borderRadius: 18,
              border: t.hairlineStrong,
              display: 'flex',
              alignItems: 'center',
              fontSize: 20,
              fontWeight: 500,
              color: 'oklch(0.8 0.015 70)',
            }}
          >
            Abbrechen
          </div>
          <div style={{ flex: 1 }} />
          <div
            className="press"
            onClick={commit}
            style={{
              height: 72,
              padding: '0 40px',
              flex: 'none',
              borderRadius: 18,
              background: value.trim() ? t.accent : 'oklch(0.3 0.012 60)',
              color: value.trim() ? t.accentInk : 'oklch(0.6 0.015 70)',
              display: 'flex',
              alignItems: 'center',
              fontSize: 22,
              fontWeight: 600,
            }}
          >
            Übernehmen
          </div>
        </div>
      </div>
    </div>
  );
}
